<template>
  <div>
    <div class="relative w-full">
      <!-- Carousel wrapper -->
      <div class="relative h-48 overflow-hidden">
        <div class="ribbon-box absolute z-[9999] right-0 top-2 py-1 px-4 font-semibold text-white">AUD ${{
          roundPrice(result.advertised_price) }}</div>
        <!-- Loop through images -->
        <div class="block w-full">
          <img :src="processedImages[currentImage] || defaultImage" alt="Hotel image"
            class="w-full h-48 object-cover" />
        </div>
      </div>
      <!-- Slider controls -->
      <button type="button"
        class="absolute top-0 left-0 z-30 flex items-center justify-center h-full px-4 cursor-pointer group focus:outline-none"
        @click="prevSlide" v-if="result.images.length > 1">
        <span
          class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/30 dark:bg-gray-800/30 group-hover:bg-white/50 dark:group-hover:bg-gray-800/60 group-focus:ring-4 group-focus:ring-white dark:group-focus:ring-gray-800/70">
          <svg class="w-4 h-4 text-white dark:text-gray-800 rtl:rotate-180" aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M5 1 1 5l4 4" />
          </svg>
          <span class="sr-only">Previous</span>
        </span>
      </button>
      <button type="button"
        class="absolute top-0 right-0 z-30 flex items-center justify-center h-full px-4 cursor-pointer group focus:outline-none"
        @click="nextSlide" v-if="result.images.length > 1">
        <span
          class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/30 dark:bg-gray-800/30 group-hover:bg-white/50 dark:group-hover:bg-gray-800/60 group-focus:ring-4 group-focus:ring-white dark:group-focus:ring-gray-800/70">
          <svg class="w-4 h-4 text-white dark:text-gray-800 rtl:rotate-180" aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m1 9 4-4-4-4" />
          </svg>
          <span class="sr-only">Next</span>
        </span>
      </button>
    </div>
    <div class="px-3 pt-3 pb-5">
      <div class="flex justify-between items-center mb-3">
        <h5 class="mb-0 font-bold  line-clamp-2 min-h-[48px] card-title">
          {{ result.listing_name || result.listing.listing_name }}
        </h5>
        <div class="flex" v-if="result.ratings">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
            class="w-5 h-5 text-yellow-600">
            <path fill-rule="evenodd"
              d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
              clip-rule="evenodd"></path>
          </svg>
          <span class="text-slate-600 ml-1.5">{{ result.ratings }}</span>
        </div>
      </div>
      <p class="text-sm text-gray-700 mb-2 line-clamp-3 min-h-[72px]">
        {{ truncateText(result.description, 70) }}
      </p>
      
        <span class="text-sm font-light text-gray-500">FROM</span>
        <p class="font-semibold mb-3">
          AUD ${{ roundPrice(result.advertised_price) }}
          <span class="font-thin">Per Night</span>
        </p>

        <a @click="(event) => ViewDetailsPage(event, result.id, result.listing_name, result.third_party_type)" 
          class="lsp-btn px-3 py-2 rounded text-sm font-medium text-center cursor-pointer">
          View More & Book
        </a>
    </div>
  </div>
</template>

<script setup>
import { ref, watchEffect } from "vue";
import { imageFunction, roundPrice } from "../../utils/imageFunction.js";
import { useSearchStore } from "../../stores/search.module.js";
import { useComponentChangeStore } from "../../stores/componentChange.module.js";
import { API_URL } from "../../src/constants.js";

const { result } = defineProps({
  result: {
    type: Object || Array,
    required: true,
  },
});

const processedImages = ref([]);
const defaultImage = API_URL + `/images/image-not-found.jpg`;
const searchStore = useSearchStore();
const componentChangeStore = useComponentChangeStore();
const currentImage = ref(0);

const nextSlide = () => {
  currentImage.value = (currentImage.value + 1) % result.images.length;
};

const prevSlide = () => {
  currentImage.value =
    (currentImage.value - 1 + result.images.length) % result.images.length;
};

const truncateText = (text, length) => {
  if (!text) return "";

  // Create a temporary DOM element to parse HTML
  const tempElement = document.createElement("div");

  // Set the innerHTML to the provided HTML string
  tempElement.innerHTML = text;

  // Extract plain text from the temporary element
  const plainText = tempElement.textContent || tempElement.innerText;

  // Truncate the plain text if it exceeds the specified length
  return plainText.length > length
    ? plainText.substring(0, length) + "..."
    : plainText;
};

const ViewDetailsPage = (event, id, name, thirdPartyType) => {
  // Prevent default behavior
  event.preventDefault();
  const actualThirdPartyType = thirdPartyType || result.third_party_type || result.listing?.third_party_type;
  // Generate a unique ID for the current tab if not already set
  const currentTabId = localStorage.getItem("currentTabId") || 
    "tab_" + Date.now() + "_" + Math.floor(Math.random() * 10000);
  
  // Store this tab's identifier
  localStorage.setItem("currentTabId", currentTabId);
  
  // Remember this tab should stay on the listing page
  localStorage.setItem(`componentLoad_${currentTabId}`, "mainView");
  
  // Create a unique ID for the new tab
  const newTabId = "tab_" + Date.now() + "_" + Math.floor(Math.random() * 10000);
  const isR2B = actualThirdPartyType && (
    actualThirdPartyType === "R2B" || 
    actualThirdPartyType === "REQUESTTOBOOK" ||
    actualThirdPartyType.toString().toUpperCase() === "R2B"
  );
  const viewName = isR2B ? "r2bView" : "hotelView";  
  // Create URL for the details page
  const detailsURL = new URL(window.location.href);
  // detailsURL.search = ''; // Removed to prevent wiping out host page query parameters like page_id
  detailsURL.searchParams.set('view', viewName);
  detailsURL.searchParams.set('propertyId', id);
  detailsURL.searchParams.set('propertyName', name);
  detailsURL.searchParams.set('tabId', newTabId);
  
  // Set properties in store for when the details page loads
  searchStore.setHotelRoomName(name);
  searchStore.setHotelRoomId(id);
  
  // Open the details page in a new tab
  window.open(detailsURL.toString(), '_blank');
};

watchEffect(() => {
  processedImages.value = imageFunction(result.images);
});
</script>
